// @ts-nocheck
import React, { memo, useState, useCallback } from 'react';
import { Handle, Position, NodeResizer } from 'reactflow';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import type { QANodeProps, QANodeData } from '../../../types/canvas';
import './QANode.css';

interface NodeConfig {
  icon: string;
  title: string;
  subtitle: string;
  theme: string;
  question: string | undefined;
  answer: string | undefined;
  questionLabel: string;
  answerLabel: string;
}

/**
 * 问答节点 — 支持 Normal QA, Quick Scan, Deep Dive, Critical, Socratic
 * 设计风格: Editorial/Scholarly - 学术期刊卡片
 */
const QANode: React.FC<QANodeProps> = ({ data, selected }) => {
  const [questionExpanded, setQuestionExpanded] = useState(false);
  const [answerExpanded, setAnswerExpanded] = useState(false);
  const { type, content } = data;

  const isAnyExpanded = questionExpanded || answerExpanded;

  const getNodeConfig = useCallback((): NodeConfig => {
    switch (type) {
      case 'qa_quick_scan':
        return {
          icon: '⚡',
          title: '快速浏览',
          subtitle: 'Quick Scan',
          theme: 'amber',
          question: 'selected_text' in content ? content.selected_text : undefined,
          answer: 'analysis' in content ? content.analysis : undefined,
          questionLabel: '原文摘录',
          answerLabel: 'AI 分析',
        };
      case 'qa_deep_dive':
        return {
          icon: '🔬',
          title: '深入理解',
          subtitle: 'Deep Dive',
          theme: 'cyan',
          question: 'selected_text' in content ? content.selected_text : undefined,
          answer: 'analysis' in content ? content.analysis : undefined,
          questionLabel: '研究对象',
          answerLabel: '深度解析',
        };
      case 'qa_critical':
        return {
          icon: '🎯',
          title: '批判性总结',
          subtitle: 'Critical Summary',
          theme: 'violet',
          question: 'selected_text' in content ? content.selected_text : ('question' in content ? content.question : undefined),
          answer: 'analysis' in content ? content.analysis : ('answer' in content ? content.answer : undefined),
          questionLabel: '分析目标',
          answerLabel: '批判总结',
        };
      case 'qa_socratic':
        return {
          icon: '💭',
          title: '苏格拉底对话',
          subtitle: 'Socratic Dialogue',
          theme: 'indigo',
          question: 'question' in content ? content.question : undefined,
          answer: 'answer' in content ? content.answer : undefined,
          questionLabel: '启发问题',
          answerLabel: '思考引导',
        };
      default:
        return {
          icon: '💬',
          title: '问答',
          subtitle: 'Q&A',
          theme: 'slate',
          question: 'question' in content ? content.question : undefined,
          answer: 'answer' in content ? content.answer : undefined,
          questionLabel: '问题',
          answerLabel: '回答',
        };
    }
  }, [type, content]);

  const config = getNodeConfig();

  const truncateText = (text: string | undefined, maxLength: number): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '…';
  };

  const questionPreview = truncateText(config.question, 120);
  const answerPreview = truncateText(config.answer, 150);

  const toggleAll = () => {
    if (isAnyExpanded) {
      setQuestionExpanded(false);
      setAnswerExpanded(false);
    } else {
      setQuestionExpanded(true);
      setAnswerExpanded(true);
    }
  };

  return (
    <div
      className={`qa-node qa-node--${config.theme} ${selected ? 'qa-node--selected' : ''} ${isAnyExpanded ? 'qa-node--expanded' : ''}`}
      data-type={type}
    >
      <NodeResizer
        color={selected ? 'var(--node-accent)' : '#94A3B8'}
        isVisible={selected}
        minWidth={280}
        minHeight={180}
      />

      <Handle type="target" position={Position.Top} className="qa-handle qa-handle--target" />

      <header
        className="qa-node__header"
        onClick={toggleAll}
        style={{ cursor: 'pointer' }}
      >
        <div className="qa-node__header-content">
          <div className="qa-node__icon-wrapper">
            <span className="qa-node__icon">{config.icon}</span>
          </div>
          <div className="qa-node__title-group">
            <h3 className="qa-node__title">{config.title}</h3>
            <span className="qa-node__subtitle">{config.subtitle}</span>
          </div>
        </div>
        <div
          className={`qa-node__toggle ${isAnyExpanded ? 'qa-node__toggle--expanded' : ''}`}
          title={isAnyExpanded ? '收起全部' : '展开全部'}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points={isAnyExpanded ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
          </svg>
        </div>
      </header>

      <div className="qa-node__body">
        <div
          className={`qa-node__section qa-node__section--question ${questionExpanded ? 'qa-node__section--expanded' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            setQuestionExpanded(!questionExpanded);
          }}
          style={{ cursor: 'pointer' }}
        >
          <div className="qa-node__section-header">
            <span className="qa-node__section-label">{config.questionLabel}</span>
            <span
              className={`qa-node__section-toggle ${questionExpanded ? 'qa-node__section-toggle--expanded' : ''}`}
              title={questionExpanded ? '收起' : '展开'}
            >
              {questionExpanded ? '▲' : '▼'}
            </span>
          </div>
          <p className="qa-node__question-text">
            {questionExpanded ? config.question : questionPreview}
          </p>
        </div>

        <div
          className={`qa-node__section qa-node__section--answer ${answerExpanded ? 'qa-node__section--expanded' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            setAnswerExpanded(!answerExpanded);
          }}
          style={{ cursor: 'pointer' }}
        >
          <div className="qa-node__section-header">
            <span className="qa-node__section-label">{config.answerLabel}</span>
            <span
              className={`qa-node__section-toggle ${answerExpanded ? 'qa-node__section-toggle--expanded' : ''}`}
              title={answerExpanded ? '收起' : '展开'}
            >
              {answerExpanded ? '▲' : '▼'}
            </span>
          </div>

          {answerExpanded ? (
            <div
              className="qa-node__answer-full"
              onClick={(e) => e.stopPropagation()}
            >
              <ReactMarkdown
                remarkPlugins={[remarkMath]}
                rehypePlugins={[rehypeKatex]}
              >
                {config.answer || '暂无分析内容'}
              </ReactMarkdown>
            </div>
          ) : (
            <p className="qa-node__answer-preview">
              {answerPreview || '暂无分析内容'}
            </p>
          )}
        </div>
      </div>

      <footer className="qa-node__footer">
        <div className="qa-node__footer-left">
          <span className="qa-node__type-badge">{config.title}</span>
        </div>
        <div className="qa-node__footer-right">
          {('source' in content && (content as Record<string, unknown>).source) ? (
            <span className="qa-node__source" title={String((content as Record<string, unknown>).source)}>
              📄 {truncateText(String((content as Record<string, unknown>).source), 15)}
            </span>
          ) : null}
        </div>
      </footer>

      <Handle type="source" position={Position.Bottom} className="qa-handle qa-handle--source" />
    </div>
  );
};

export default memo(QANode);
